import React, { useState } from 'react';
import PropTypes from 'prop-types';

import QuantitySelector from 'components/commons/QuantitySelector';
import BestPriceDisplay from 'components/commons/BestPriceDisplay';
import { productPropTypes } from 'helpers/propTypes';
import { formatCurrencyARS } from 'helpers/currency';

import './ItemD7.scss';

const ItemD7 = ({
  colors = [],
  combinations = {},
  combinationsTalle = {},
  item,
  onItemClick,
  onBuyClick,
  onQuantityChange,
  quantity,
  talles = [],
  getValidPriceRange,
  show,
  cantMedidasMax = 2,
  renderInstallmentsBadge,
  paymentMethods,
  autoDiscounts,
}) => {
  const unitPrice = item.sale > 0 ? item.sale : item.price;
  const [selectedColor, setSelectedColor] = useState(null);
  const [tallesXColor, setTallesXColor] = useState(null);
  const [intervalId, setIntervalId] = useState(null);

  const selectedColorImage = selectedColor ? (selectedColor.image_2 || selectedColor.image_1) : item.image1;
  const imageSource = selectedColor ? (selectedColorImage || item.image1) : item.image1;
  const hoverImageSource = selectedColor
    ? (selectedColor.image_1 && selectedColor.image_2 ? selectedColor.image_1 : (item.image2 || null))
    : (item.image2 || null);

  const hasNonNullPrice = talles.some((t) => t.price !== null && t?.price > 0);
  const hasSale = Number(item.sale) > 0;
  const currentPrice = hasSale ? item.sale : item.price;
  const oldPrice = hasSale ? item.price : null;
  const discountPct = hasSale ? Math.round(100 - (item.sale / item.price) * 100) : null;

  const hasCombos = Array.isArray(item.product_wholesaler) && item.product_wholesaler.length > 0;
  const hasDescriptions = Array.isArray(item.stock_product_descriptions) && item.stock_product_descriptions.length > 0;
  const canBuyDirect = !hasCombos && !hasDescriptions && item.stock > 0;

  const colorsReducidos = colors.reduce((acc, obj) => {
    const exists = acc.some(({ stock_description_id }) => stock_description_id === obj.stock_description_id);
    if (!exists) acc.push(obj);
    return acc;
  }, []);

  const handleColorSelect = (color, descriptions) => {
    setSelectedColor(color);
    setTallesXColor(descriptions);
  };

  const handleMouseEnter = () => {
    let i = -1;
    const id = setInterval(() => {
      if (i < colorsReducidos.length && i !== -1) {
        setSelectedColor(colorsReducidos[i]);
        i += 1;
      } else {
        setSelectedColor(null);
        i = 0;
      }
    }, 1500);
    setIntervalId(id);
  };
  const handleMouseLeave = () => {
    clearInterval(intervalId);
    setIntervalId(null);
    setSelectedColor(null);
  };

  const dots = (w) => (w?.length > 60 ? `${w.substring(0, 58)}...` : w);

  const getOverlayNode = () => {
    if (hasCombos) return <button type="button" className="itd7-btn" onClick={onItemClick}>Ver combos</button>;
    if (hasDescriptions) return null;
    if (!canBuyDirect && item.stock <= 0) return <span className="itd7-out">Sin stock</span>;
    return null;
  };

  const renderBlankMeasureSpace = () => {
    const hasColors = colorsReducidos.length > 0;
    const tallesCount = tallesXColor?.length ?? (talles?.length ?? 0);
    const hasTalles = tallesCount > 0;
    const onlyColors = hasColors && !hasTalles;

    const baseRows = (Number(cantMedidasMax) || 0) + 1;
    const adjustedRows = Math.max(0, baseRows - (hasColors ? 1 : 0) - (hasTalles ? 1 : 0));
    if (adjustedRows === 0) return null;

    return (
      <div className="itd7-filler">
        {Array.from({ length: adjustedRows }).map((_, i) => (
          <div key={i} className={`itd7-filler__row ${onlyColors ? 'onlycolors' : ''}`} />
        ))}
      </div>
    );
  };

  const categoryName = item?.categories?.[0]?.name;
  const overlayNode = getOverlayNode();

  const handleBuy = (e) => {
    e.stopPropagation();
    (typeof onBuyClick === 'function' ? onBuyClick : onItemClick)();
  };

  return (
    <article className="itd7-card" data-testid={`product-${item.id}`}>
      {hasSale && <span className="itd7-badge">-{discountPct}%</span>}

      <div
        className="itd7-media"
        onClick={onItemClick}
        role="presentation"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <img
          className={`itd7-img${hoverImageSource ? ' itd7-img--primary' : ''}`}
          src={imageSource}
          alt={item.name}
          style={{ objectFit: 'contain', objectPosition: 'center' }} // inline para que no lo pise ningun SCSS legacy
        />
        {!!hoverImageSource && (
          <img
            className="itd7-img itd7-img--hover"
            src={hoverImageSource}
            alt=""
            aria-hidden="true"
            style={{ objectFit: 'contain', objectPosition: 'center' }}
          />
        )}
        {overlayNode && <div className="itd7-overlay">{overlayNode}</div>}
      </div>

      <div className="itd7-body">
        {categoryName && <div className="itd7-cat">{categoryName}</div>}

        <h3 className="itd7-title" onClick={onItemClick} role="presentation">
          {dots(item.name)}
        </h3>

        <div className="itd7-badges">
          {typeof renderInstallmentsBadge === 'function' ? renderInstallmentsBadge() : null}
        </div>

        {colorsReducidos.length > 0 && (
          <div className="itd7-swatches">
            {colorsReducidos.slice(0, 5).map((c) => {
              const isMulti = c?.stock_descriptions?.name === 'Multicolor';
              const bg = isMulti ? {} : { backgroundColor: c?.stock_descriptions?.rgb };
              const active = selectedColor?.stock_description_id === c.stock_description_id;
              return (
                <span
                  key={`sw-${c.stock_description_id}`}
                  className={`itd7-swatch${active ? ' active' : ''}${isMulti ? ' multi' : ''}`}
                  style={bg}
                  role="presentation"
                  onClick={() => handleColorSelect(c, combinations[c.stock_description_id])}
                />
              );
            })}
            {colorsReducidos.length > 5 && <span className="itd7-more">+{colorsReducidos.length - 5}</span>}
          </div>
        )}

        <div className="itd7-sizes">
          {talles.length > 0 && (
            <>
              {!tallesXColor
                ? Object.keys(combinationsTalle || {}).map((id) => {
                    const talle = talles.find(({ stock_description_id }) => stock_description_id === Number(id));
                    if (!talle) return null;
                    return (
                      <span key={`t-${talle.stock_description_id}`} className="itd7-size">
                        {talle.stock_descriptions?.name}
                      </span>
                    );
                  })
                : tallesXColor.map((talle) => (
                    <span key={`t-${talle.stock_description_id}`} className="itd7-size">
                      {talle.stock_descriptions?.name}
                    </span>
                  ))}
            </>
          )}
        </div>

        {Number(cantMedidasMax) > 0 && renderBlankMeasureSpace()}

        {canBuyDirect && (
          <div className="itd7-inlineqty">
            <QuantitySelector limit={item.stock} onQuantityChange={onQuantityChange} quantity={quantity} show={show} />
          </div>
        )}

        <div className="itd7-prices">
          {!hasNonNullPrice ? (
            <>
              <span className="itd7-price">{formatCurrencyARS(currentPrice)}</span>
              {oldPrice ? <span className="itd7-old">{formatCurrencyARS(oldPrice)}</span> : null}
            </>
          ) : (
            <span className="itd7-price itd7-range">{getValidPriceRange()}</span>
          )}
        </div>
        <BestPriceDisplay 
          unitPrice={unitPrice} 
          autoDiscounts={autoDiscounts} 
          paymentMethods={paymentMethods} 
          show={show}
          isItem={true}
        />

        <button type="button" className="api-buy-btn" onClick={handleBuy}>
          Comprar
        </button>
      </div>
    </article>
  );
};

ItemD7.defaultProps = { combinations: {}, combinationsTalle: {}, colors: [], talles: [] };
ItemD7.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.shape()),
  combinations: PropTypes.shape(),
  combinationsTalle: PropTypes.shape(),
  item: productPropTypes.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onBuyClick: PropTypes.func,
  onQuantityChange: PropTypes.func.isRequired,
  quantity: PropTypes.number.isRequired,
  talles: PropTypes.arrayOf(PropTypes.shape()),
  getValidPriceRange: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  cantMedidasMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  renderInstallmentsBadge: PropTypes.func,
};

export default ItemD7;
